import { SocketMessageEvents } from '@shared/constants/campaign.constants';
import { CallDto } from '@shared/services/apiService/apiService';
import { SortConfig } from '@pages/Admin/Campaign/ui/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useGetCalls } from './useGetCalls';
import { CallsFilterFormValue } from '../model/type';
import { resolveSocketIoConnection } from '@shared/utils/serviceUrl';

const { url: WS_URL, path: WS_PATH } = resolveSocketIoConnection(import.meta.env.VITE_API_URL);

interface WsException {
  status: string;
  message: string;
}

export const useWebSocketGetCalls = (
  campaignId: string,
  params: CallsFilterFormValue,
  sortConfig: SortConfig,
  onError?: <T>(error: T) => void,
) => {
  const [isConnected, setIsConnected] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);

  const {
    recipientRefetch,
    callsData,
    upsertCall,
    recipientIsLoading: isLoading,
    hasMore,
    loadMore,
    totalItems,
    isFetching,
  } = useGetCalls(campaignId, params, sortConfig);

  const token = useMemo(() => localStorage.getItem('accessToken')!, []);

  const subcribeToCalls = useCallback(
    (campaignId: string) => {
      socket?.emit(SocketMessageEvents.SUB_TO_GET_CALLS, {
        campaignId: campaignId,
      });
    },
    [socket],
  );

  useEffect(() => {
    if (!token) return;

    const newSocket = io(`${WS_URL}/conversations`, {
      path: `${WS_PATH}/socket.io`,
      transports: ['websocket'],
      autoConnect: true,
      auth: { token },
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
      setSocket(null);
    };
  }, [token]);

  useEffect(() => {
    if (!socket) return;

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    const onGetCalls = (value: { result: CallDto }) => {
      upsertCall(value.result);
    };

    const onException = (error: WsException) => {
      socket.disconnect();
      console.error(error);
      onError?.(error);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on(SocketMessageEvents.GET_CALLS, onGetCalls);
    socket.on('exception', onException);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off(SocketMessageEvents.GET_CALLS, onGetCalls);
      socket.off('exception', onException);
    };
  }, [socket, onError, upsertCall]);

  return {
    isConnected,
    isLoading,
    isFetching,
    hasMore,
    loadMore,
    totalItems,
    recipients: callsData,
    subcribeToCalls,
    recipientRefetch,
  };
};
